import './style.modules.scss'

import {
  BrandIcon,
  BrandIconColor,
  BrandIconId,
  SpacerSpacing
} from '@ampcore/adel-react'
import { VideoDescription, VideoPlayer } from '@ampcore/video-player'
import React from 'react'
import xss from 'xss'

import { rteContentFilter } from '../../lib/helpers/rteContentFilter'
import { wrapResponsiveTables } from '../../lib/helpers/wrapResponsiveTables'
import {
  CertificateRates,
  CertificateRatesProps
} from '../CertificateRates/CertificateRates'
import { TwoColumnBannerBackgroundColor } from './TwoColumnBannerColor.type'

declare global {
  interface Window {
    BRIGHTCOVE_PK?: string
    BRIGHTCOVE_ACC?: string
  }
}

export type TwoColumnBannerProps = {
  psnAnalytics?: string
  backgroundImage?: string
  backgroundColor?: TwoColumnBannerBackgroundColor
  componentSpacing?: SpacerSpacing
  textColor?: string
  layoutConfiguration?: string
  leftIcon?: BrandIconId
  leftIconColor?: BrandIconColor
  fillLeft?: boolean
  strokeLeft?: boolean
  leftEyebrowImage?: string
  leftEyebrowImageAlt?: string
  leftEyebrowText?: string
  leftPlaylistId?: string
  leftColumnContent?: string
  leftCertificateRates?: CertificateRatesProps
  leftPrimaryBtnText?: string
  leftPrimaryBtnUrl?: string
  leftPrimaryBtnTarget?: string
  leftPrimaryBtnAnalytics?: string
  leftTertiaryBtnText?: string
  leftTertiaryBtnUrl?: string
  leftTertiaryBtnTarget?: string
  leftTertiaryBtnAnalytics?: string
  leftBackgroundColor?: string
  rightIcon?: BrandIconId
  rightIconColor?: BrandIconColor
  fillRight?: boolean
  strokeRight?: boolean
  rightEyebrowImage?: string
  rightEyebrowText?: string
  rightEyebrowImageAlt?: string
  rightPlaylistId?: string
  rightColumnContent?: string
  rightCertificateRates?: CertificateRatesProps
  rightPrimaryBtnText?: string
  rightPrimaryBtnUrl?: string
  rightPrimaryBtnTarget?: string
  rightPrimaryBtnAnalytics?: string
  rightTertiaryBtnText?: string
  rightTertiaryBtnUrl?: string
  rightTertiaryBtnTarget?: string
  rightTertiaryBtnAnalytics?: string
  rightBackgroundColor?: string
  btnColor?: string
  testId?: string
}

export const renderCertificateRates = (
  column: 'left' | 'right',
  certificateRates?: CertificateRatesProps
) => {
  if (!certificateRates) return null

  return (
    <CertificateRates
      key={`${column}-certificate-rates`}
      {...certificateRates}
    />
  )
}

type Side = 'left' | 'right'

type IconEyebrowImageArgs = {
  side: Side
  icon?: BrandIconId
  iconColor?: BrandIconColor
  fill?: boolean
  stroke?: boolean
  eyebrowImage?: string
  eyebrowImageAlt?: string
  containerClassName: string
  testId?: string
}

// Icon + eyebrow-image block. `containerClassName` differs per side (the right
// column adds padding-left classes when only the left column holds an image),
// so the caller computes it rather than this helper re-deriving layout state.
export const renderIconEyebrowImage = ({
  side,
  icon,
  iconColor,
  fill,
  stroke,
  eyebrowImage,
  eyebrowImageAlt,
  containerClassName,
  testId
}: IconEyebrowImageArgs) => (
  <div className={containerClassName}>
    {icon && (
      <BrandIcon
        icon={icon}
        color={iconColor}
        fill={!!fill}
        stroke={!!stroke}
        className="u-marginBottom8"
        testId={testId ? `${testId}-${side}-brand-icon` : undefined}
      />
    )}
    {eyebrowImage && (
      <img
        src={eyebrowImage}
        alt={eyebrowImageAlt ?? ''}
        className="TwoColumnBanner-eyebrowImage u-paddingVert8"
        data-testid={testId ? `${testId}-${side}-eyebrow-image` : null}
      />
    )}
  </div>
)

type EyebrowTextArgs = {
  side: Side
  eyebrowText?: string
  className: string
  testId?: string
}

// Eyebrow-text paragraph. HTML is sanitized with xss before render.
export const renderEyebrowText = ({
  side,
  eyebrowText,
  className,
  testId
}: EyebrowTextArgs) => {
  if (!eyebrowText) return null

  return (
    <p
      className={className}
      dangerouslySetInnerHTML={{
        __html: xss(eyebrowText)
      }}
      data-testid={testId ? `${testId}-${side}-eyebrow-text` : null}
    />
  )
}

type InnerContentArgs = {
  side: Side
  containerClassName: string
  columnContent?: string
  hasColumnTable?: boolean
  backgroundColor?: string
  textColor?: string
  // Playlist that renders a VideoDescription in this column when the column has
  // no RTE content — the opposite side's playlist (description mirrors the video).
  descriptionPlaylistId?: string
  descriptionContainerClassName: string
  // Playlist that renders the VideoPlayer in this column.
  playerPlaylistId?: string
  testId?: string
}

// Inner content block: video description, video player, or sanitized RTE
// content for one side. RTE HTML passes through rteContentFilter before render.
export const renderInnerContent = ({
  side,
  containerClassName,
  columnContent,
  hasColumnTable,
  descriptionPlaylistId,
  descriptionContainerClassName,
  playerPlaylistId,
  testId
}: InnerContentArgs) => (
  <div
    className={containerClassName}
    data-testid={testId ? `${testId}-${side}-content` : null}
  >
    {descriptionPlaylistId && !columnContent && (
      <div className={descriptionContainerClassName}>
        <VideoDescription
          accountId={window.BRIGHTCOVE_ACC || ''}
          policyKey={window.BRIGHTCOVE_PK || ''}
          playlistId={descriptionPlaylistId}
          testId={testId ? `${testId}-${side}-video-description` : undefined}
        />
      </div>
    )}
    {playerPlaylistId && (
      <div className="TwoColumnBanner-videoContainer">
        <VideoPlayer
          accountId={window.BRIGHTCOVE_ACC || ''}
          policyKey={window.BRIGHTCOVE_PK || ''}
          playlistId={playerPlaylistId}
          testId={testId ? `${testId}-${side}-video` : undefined}
        />
      </div>
    )}
    {!playerPlaylistId && columnContent && (
      <div
        className={`TwoColumnBanner-rteContent ${hasColumnTable ? 'Table-compound' : ''}`}
        dangerouslySetInnerHTML={{
          __html: rteContentFilter(wrapResponsiveTables(columnContent))
        }}
      />
    )}
  </div>
)

// `btnColor === 'white'` reverses the buttons onto a dark surface. The computed
// class names are identical for both sides, so both columns share them.
export const getButtonClassNames = (btnColor?: string) => {
  const isReversedBtn = btnColor === 'white'
  const primaryBtnClassName = [
    isReversedBtn && 'Button--reversed',
    'u-marginRight8 u-sm-marginRight0 u-sm-marginBottom8'
  ]
    .filter(Boolean)
    .join(' ')
  const tertiaryBtnClassName = isReversedBtn ? 'Button--reversed' : undefined

  return { primaryBtnClassName, tertiaryBtnClassName }
}
